import {z} from 'zod';
import {
  AccountFilterCondition,
  AccountFilterConditionSchema,
  AccountFilter,
  AccountFilterGroup,
} from '../../shared/filter/accountFilter';
import {zodTypeguard} from '../../shared/api/helpers';

// ============================================================================
// Client-side "Draft" types for filter editing
// ============================================================================
// These types extend the API types to allow undefined conditions, representing
// untyped filters (user hasn't selected a type yet). When sending to the API,
// undefined conditions are filtered out.

// Draft version of AccountFilterCondition - allows undefined for untyped filters
const AccountFilterConditionDraftSchema =
  AccountFilterConditionSchema.optional();
export type AccountFilterConditionDraft = z.infer<
  typeof AccountFilterConditionDraftSchema
>;

// Wrapper for a condition with an ID (for stable React keys)
const FilterConditionWithIdSchema = z.object({
  id: z.string(),
  item: AccountFilterConditionDraftSchema,
});
export type FilterConditionWithId = z.infer<typeof FilterConditionWithIdSchema>;

// A FilterConditionWithId where the item is set (not undefined)
export type SetFilterCondition = FilterConditionWithId & {
  item: AccountFilterCondition;
};

// Draft version of AccountFilterGroup - has id directly and contains conditions with IDs
const AccountFilterGroupDraftSchema = z.object({
  id: z.string(),
  operator: z.enum(['and', 'or']),
  conditions: z.array(FilterConditionWithIdSchema),
});
export type AccountFilterGroupDraft = z.infer<
  typeof AccountFilterGroupDraftSchema
>;
export const isAccountFilterGroupDraft = zodTypeguard(
  AccountFilterGroupDraftSchema
);

// Top-level filter items can be either a condition with ID or a group
export type FilterItemWithId = FilterConditionWithId | AccountFilterGroupDraft;

// ============================================================================
// Utility functions
// ============================================================================

/**
 * Check if a filter condition has been set (has a non-empty value)
 */
export const isConditionSet = (condition: AccountFilterCondition): boolean => {
  if (condition.operator === 'in') {
    return Array.isArray(condition.value) && condition.value.length > 0;
  }

  if (condition.operator === 'range') {
    return (
      condition.value.min !== undefined || condition.value.max !== undefined
    );
  }

  // For 'equals' and 'exists' operators, always consider it set
  return true;
};

/**
 * Check if a filter item has been set (has meaningful data)
 * Returns false for undefined items (untyped filters)
 */
export const isFilterItemSet = (
  filterItemWithId: FilterItemWithId
): filterItemWithId is SetFilterCondition | AccountFilterGroupDraft => {
  // If it's a group, check if it has at least one set condition
  if (isAccountFilterGroupDraft(filterItemWithId)) {
    return filterItemWithId.conditions.some(
      (c) => c.item !== undefined && isConditionSet(c.item)
    );
  }

  // It's a FilterConditionWithId - check if item is set
  if (!filterItemWithId.item) {
    return false; // undefined = not set
  }

  // Single condition
  return isConditionSet(filterItemWithId.item);
};

// ============================================================================
// Conversion functions
// ============================================================================

/**
 * Convert an AccountFilter to the draft filter items format used internally.
 */
export function accountFilterToFilterItems(filter: AccountFilter): {
  filterItems: FilterItemWithId[];
  filterOperator: 'and' | 'or';
} {
  const filterItems: FilterItemWithId[] =
    filter.conditions?.items.map((item) => {
      if (item.type === 'group') {
        return {
          id: crypto.randomUUID(),
          operator: item.operator,
          conditions: item.conditions.map((condition) => ({
            id: crypto.randomUUID(),
            item: condition,
          })),
        } satisfies AccountFilterGroupDraft;
      } else {
        return {
          id: crypto.randomUUID(),
          item: item.condition,
        } satisfies FilterConditionWithId;
      }
    }) ?? [];

  return {
    filterItems,
    filterOperator: filter.conditions?.operator ?? 'and',
  };
}

/**
 * Convert draft filter items to API-ready conditions array.
 */
export function filterItemsToConditions(
  filterItems: FilterItemWithId[],
  filterOperator: 'and' | 'or'
): {
  items: Array<AccountFilterCondition | AccountFilterGroup>;
  operator: 'and' | 'or' | undefined;
} {
  const items: Array<AccountFilterCondition | AccountFilterGroup> = filterItems
    .filter(isFilterItemSet)
    .map((filterItemWithId) => {
      if (isAccountFilterGroupDraft(filterItemWithId)) {
        const conditions = filterItemWithId.conditions
          .filter((c): c is SetFilterCondition => c.item !== undefined)
          .map((c) => c.item);

        return {
          // operator doesn't matter if there's only one condition (prevents unnecessary queries)
          operator: conditions.length >= 2 ? filterItemWithId.operator : 'and',
          conditions,
        } satisfies AccountFilterGroup;
      }
      return filterItemWithId.item;
    });

  // prevent unnecessary queries (operator only matters if there are multiple items)
  const operator = items.length >= 2 ? filterOperator : undefined;

  return {items, operator};
}
